import { CompatibilityConfig } from '../types';

export interface EventHandlers {
	onMouseDown?: (e: MouseEvent) => void;
	onMouseMove?: (e: MouseEvent) => void;
	onMouseUp?: (e: MouseEvent) => void;
	onMouseLeave?: (e: MouseEvent) => void;

	onTouchStart?: (e: TouchEvent) => void;
	onTouchMove?: (e: TouchEvent) => void;
	onTouchEnd?: (e: TouchEvent) => void;
	onTouchCancel?: (e: TouchEvent) => void;

	onPointerDown?: (e: PointerEvent) => void;
	onPointerMove?: (e: PointerEvent) => void;
	onPointerUp?: (e: PointerEvent) => void;
	onPointerCancel?: (e: PointerEvent) => void;

	onClick?: (e: MouseEvent) => void;
	onContextMenu?: (e: MouseEvent) => void;
}

export interface GlobalEventHandlers {
	onMouseMove?: (e: MouseEvent) => void;
	onMouseUp?: (e: MouseEvent) => void;
	onTouchMove?: (e: TouchEvent) => void;
	onTouchEnd?: (e: TouchEvent) => void;
	onTouchCancel?: (e: TouchEvent) => void;
	onPointerMove?: (e: PointerEvent) => void;
	onPointerUp?: (e: PointerEvent) => void;
	onPointerCancel?: (e: PointerEvent) => void;
}

export class EventManager {
	private static instance: EventManager;

	static getInstance(compatibility?: CompatibilityConfig): EventManager {
		if (!EventManager.instance) {
			EventManager.instance = new EventManager(compatibility);
		}
		return EventManager.instance;
	}

	private compatibility: CompatibilityConfig;
	private hasPointerEvents: boolean;

	// Track dragging state to block pull-to-refresh
	private isDraggingActive = false;

	private hasTouchEvents: boolean;

	private draggableHandlers = new Map<HTMLElement, EventHandlers>();
	private globalHandlers: GlobalEventHandlers = {};

	private isGlobalListening = false;

	private constructor(compatibility?: CompatibilityConfig) {
		this.compatibility = compatibility || {};

		this.hasPointerEvents = 'PointerEvent' in window;
		this.hasTouchEvents = 'ontouchstart' in window;

		if (this.compatibility.usePointerEvents !== undefined) {
			this.hasPointerEvents = this.compatibility.usePointerEvents;
		}
	}

	/**
	 * Update dragging state for pull-to-refresh blocking
	 * TODO: Make this configurable via compatibility settings
	 */
	setDraggingState(isActive: boolean): void {
		this.isDraggingActive = isActive;

		// Re-register touchmove with correct passive option
		// passive: false is required for preventDefault() to work
		if (this.hasTouchEvents) {
			this.updateGlobalTouchMoveListener();
		}
	}

	/**
	 * Регистрация обработчиков для draggable элемента
	 */
	registerDraggable(element: HTMLElement, handlers: EventHandlers): void {
		this.draggableHandlers.set(element, handlers);

		if (this.hasPointerEvents) {
			// Pointer Events (универсальные) - одна функция на все элементы
			element.addEventListener('pointerdown', this.handlePointerDown);
			element.addEventListener('pointermove', this.handlePointerMove);
			element.addEventListener('pointerup', this.handlePointerUp);
			element.addEventListener('pointercancel', this.handlePointerCancel);
		} else {
			// Fallback: раздельные mouse и touch события
			element.addEventListener('mousedown', this.handleMouseDown);
			element.addEventListener('mousemove', this.handleMouseMove);
			element.addEventListener('mouseup', this.handleMouseUp);
			element.addEventListener('mouseleave', this.handleMouseLeave);

			if (this.hasTouchEvents) {
				const passive = this.compatibility.passiveEvents !== false;
				element.addEventListener('touchstart', this.handleTouchStart, { passive });
				element.addEventListener('touchmove', this.handleTouchMove, { passive });
				element.addEventListener('touchend', this.handleTouchEnd);
				element.addEventListener('touchcancel', this.handleTouchCancel);
			}
		}

		// Дополнительные обработчики
		element.addEventListener('click', this.handleClick);
		element.addEventListener('contextmenu', this.handleContextMenu);
	}

	/**
	 * Удаление обработчиков для draggable элемента
	 */
	unregisterDraggable(element: HTMLElement): void {
		if (!this.draggableHandlers.has(element)) return;

		if (this.hasPointerEvents) {
			element.removeEventListener('pointerdown', this.handlePointerDown);
			element.removeEventListener('pointermove', this.handlePointerMove);
			element.removeEventListener('pointerup', this.handlePointerUp);
			element.removeEventListener('pointercancel', this.handlePointerCancel);
		} else {
			element.removeEventListener('mousedown', this.handleMouseDown);
			element.removeEventListener('mousemove', this.handleMouseMove);
			element.removeEventListener('mouseup', this.handleMouseUp);
			element.removeEventListener('mouseleave', this.handleMouseLeave);

			if (this.hasTouchEvents) {
				element.removeEventListener('touchstart', this.handleTouchStart);
				element.removeEventListener('touchmove', this.handleTouchMove);
				element.removeEventListener('touchend', this.handleTouchEnd);
				element.removeEventListener('touchcancel', this.handleTouchCancel);
			}
		}

		// Дополнительные обработчики
		element.removeEventListener('click', this.handleClick);
		element.removeEventListener('contextmenu', this.handleContextMenu);

		this.draggableHandlers.delete(element);
	}

	/**
	 * Регистрация глобальных обработчиков (для отслеживания движения)
	 */
	registerGlobal(handlers: GlobalEventHandlers): void {
		this.globalHandlers = handlers;

		if (!this.isGlobalListening) {
			this.startGlobalListening();
		}
	}

	/**
	 * Очистка глобальных обработчиков
	 */
	clearGlobal(): void {
		this.globalHandlers = {};
		this.stopGlobalListening();
	}

	/**
	 * Получение текущей стратегии событий
	 */
	getEventStrategy(): 'pointer' | 'mouse-touch' {
		return this.hasPointerEvents ? 'pointer' : 'mouse-touch';
	}

	/**
	 * Проверка поддержки Pointer Events
	 */
	supportsPointerEvents(): boolean {
		return this.hasPointerEvents;
	}

	/**
	 * Полная очистка всех обработчиков
	 */
	destroy(): void {
		// Удаляем все обработчики с элементов
		for (const element of this.draggableHandlers.keys()) {
			this.unregisterDraggable(element);
		}

		// Очищаем глобальные обработчики
		this.clearGlobal();

		// Очищаем Map
		this.draggableHandlers.clear();
	}

	// ========== PRIVATE METHODS ==========

	private startGlobalListening(): void {
		if (this.isGlobalListening) return;

		if (this.hasPointerEvents) {
			document.addEventListener('pointermove', this.handleGlobalPointerMove);
			document.addEventListener('pointerup', this.handleGlobalPointerUp);
			document.addEventListener('pointercancel', this.handleGlobalPointerCancel);
		} else {
			document.addEventListener('mousemove', this.handleGlobalMouseMove);
			document.addEventListener('mouseup', this.handleGlobalMouseUp);

			if (this.hasTouchEvents) {
				// Use passive: false when dragging to allow preventDefault()
				const passive = !this.isDraggingActive && (this.compatibility.passiveEvents !== false);
				document.addEventListener('touchmove', this.handleGlobalTouchMove, { passive });
				document.addEventListener('touchend', this.handleGlobalTouchEnd);
				document.addEventListener('touchcancel', this.handleGlobalTouchCancel);
			}
		}

		this.isGlobalListening = true;
	}

	private stopGlobalListening(): void {
		if (!this.isGlobalListening) return;

		if (this.hasPointerEvents) {
			document.removeEventListener('pointermove', this.handleGlobalPointerMove);
			document.removeEventListener('pointerup', this.handleGlobalPointerUp);
			document.removeEventListener('pointercancel', this.handleGlobalPointerCancel);
		} else {
			document.removeEventListener('mousemove', this.handleGlobalMouseMove);
			document.removeEventListener('mouseup', this.handleGlobalMouseUp);

			if (this.hasTouchEvents) {
				document.removeEventListener('touchmove', this.handleGlobalTouchMove);
				document.removeEventListener('touchend', this.handleGlobalTouchEnd);
				document.removeEventListener('touchcancel', this.handleGlobalTouchCancel);
			}
		}

		this.isGlobalListening = false;
	}

	// ========== EVENT HANDLERS ==========

	// Pointer Events - стрелочные функции, получают элемент из e.currentTarget
	private handlePointerDown = (e: PointerEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;

		e.preventDefault();
		e.stopPropagation();

		handlers.onPointerDown?.(e);

		// Также вызываем mouse/touch обработчики для совместимости
		if (e.pointerType === 'mouse') {
			this.handleMouseDown(e as unknown as MouseEvent);
		} else if (e.pointerType === 'touch') {
			this.handleTouchStart(e as unknown as TouchEvent);
		}
	};

	private handlePointerMove = (e: PointerEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onPointerMove?.(e);
	};

	private handlePointerUp = (e: PointerEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onPointerUp?.(e);
	};

	private handlePointerCancel = (e: PointerEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onPointerCancel?.(e);
	};

	// Mouse Events
	private handleMouseDown = (e: MouseEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;

		if (e.button === 0) { // Левая кнопка мыши
			e.preventDefault();
			e.stopPropagation();
		}

		handlers.onMouseDown?.(e);
	};

	private handleMouseMove = (e: MouseEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onMouseMove?.(e);
	};

	private handleMouseUp = (e: MouseEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onMouseUp?.(e);
	};

	private handleMouseLeave = (e: MouseEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onMouseLeave?.(e);
	};

	// Touch Events
	private handleTouchStart = (e: TouchEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;

		e.stopPropagation();
		handlers.onTouchStart?.(e);
	};

	private handleTouchMove = (e: TouchEvent): void => {
		// Block pull-to-refresh during DnD
		if (this.isDraggingActive && e.cancelable) {
			e.preventDefault();
		}

		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onTouchMove?.(e);
	};

	private handleTouchEnd = (e: TouchEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onTouchEnd?.(e);
	};

	private handleTouchCancel = (e: TouchEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onTouchCancel?.(e);
	};

	// Дополнительные события
	private handleClick = (e: MouseEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;
		handlers.onClick?.(e);
	};

	private handleContextMenu = (e: MouseEvent): void => {
		const element = e.currentTarget as HTMLElement;
		if (!this.draggableHandlers.has(element)) return;

		const handlers = this.draggableHandlers.get(element)!;

		e.preventDefault();
		e.stopPropagation();
		handlers.onContextMenu?.(e);
	};

	private updateGlobalTouchMoveListener(): void {
		// Remove and re-add with correct passive option
		document.removeEventListener('touchmove', this.handleGlobalTouchMove);

		const passive = !this.isDraggingActive && (this.compatibility.passiveEvents !== false);
		document.addEventListener('touchmove', this.handleGlobalTouchMove, { passive });
	}


	// Глобальные обработчики - они не привязаны к конкретному элементу
	private handleGlobalPointerMove = (e: PointerEvent): void => {
		this.globalHandlers.onPointerMove?.(e);
	};

	private handleGlobalPointerUp = (e: PointerEvent): void => {
		this.globalHandlers.onPointerUp?.(e);
	};

	private handleGlobalPointerCancel = (e: PointerEvent): void => {
		this.globalHandlers.onPointerCancel?.(e);
	};

	private handleGlobalMouseMove = (e: MouseEvent): void => {
		this.globalHandlers.onMouseMove?.(e);
	};

	private handleGlobalMouseUp = (e: MouseEvent): void => {
		this.globalHandlers.onMouseUp?.(e);
	};

	private handleGlobalTouchMove = (e: TouchEvent): void => {
		// Block pull-to-refresh during DnD
		if (this.isDraggingActive && e.cancelable) {
			e.preventDefault();
		}

		this.globalHandlers.onTouchMove?.(e);
	};

	private handleGlobalTouchEnd = (e: TouchEvent): void => {
		this.globalHandlers.onTouchEnd?.(e);
	};

	private handleGlobalTouchCancel = (e: TouchEvent): void => {
		this.globalHandlers.onTouchCancel?.(e);
	};
}
